import { useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2, X } from 'lucide-react';

/** Colors aligned with ann `Tag` (`tag.styled.js` / `tagColorTokens`). Includes `dark:` for shell surfaces. */
const variantClasses = {
  /** Same as ann Tag `neutral`. */
  default:
    'border border-[#5C5C5C] bg-[#F2F2F2] text-[#3A3A3A] dark:border-gray-500 dark:bg-gray-800 dark:text-gray-100',
  neutral:
    'border border-[#5C5C5C] bg-[#F2F2F2] text-[#3A3A3A] dark:border-gray-500 dark:bg-gray-800 dark:text-gray-100',
  primary:
    'border-none bg-[linear-gradient(135deg,#7c5cfc,#5b8def)] text-white',
  classicPrimary:
    'border border-[#2173FF] bg-[#2173FF] text-[#FFFFFF] dark:border-blue-500 dark:bg-blue-600 dark:text-white',
  secondary:
    'border border-[#5C5C5C] bg-[#FFFFFF] text-[#3A3A3A] dark:border-gray-500 dark:bg-gray-900 dark:text-gray-100',
  success:
    'border border-[#009978] bg-[#E5FDF8] text-[#003328] dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-100',
  informative:
    'border border-[#2173FF] bg-[#E9F1FF] text-[#003328] dark:border-blue-500 dark:bg-blue-950/50 dark:text-blue-100',
  error:
    'border border-[#D32F2F] bg-[#FDEDED] text-[#5F2120] dark:border-red-500 dark:bg-red-950/40 dark:text-red-100',
  danger:
    'border border-[#D32F2F] bg-[#FDEDED] text-[#5F2120] dark:border-red-500 dark:bg-red-950/40 dark:text-red-100',
  alert:
    'border border-[#B25102] bg-[#FFF4E5] text-[#663C00] dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-100',
  warning:
    'border border-[#B25102] bg-[#FFF4E5] text-[#663C00] dark:border-amber-600 dark:bg-amber-950/40 dark:text-amber-100',
  limeGreen:
    'border border-[#A0DA1D] bg-[#F9FEE5] text-[#3E5C0A] dark:border-lime-500 dark:bg-lime-950/30 dark:text-lime-100',
  yellow:
    'border border-[#FF9900] bg-[#FFFAF0] text-[#5C3500] dark:border-amber-500 dark:bg-amber-950/35 dark:text-amber-100',
  purple:
    'border border-[#B070F5] bg-[#F3E9FD] text-[#2C075C] dark:border-purple-500 dark:bg-purple-950/40 dark:text-purple-100',
  lightBlue:
    'border border-[#00B5D9] bg-[#E5F8FF] text-[#02445A] dark:border-sky-500 dark:bg-sky-950/40 dark:text-sky-100',
  orange:
    'border border-[#FF7A1A] bg-[#FFF1E5] text-[#7A2E00] dark:border-orange-500 dark:bg-orange-950/40 dark:text-orange-100',
};

/** Matches ann `Tag`: padding 4px 8px, height 20px / 24px, gap 4px / 8px. */
const sizeClasses = {
  medium: 'h-6 gap-2 px-2 py-1 text-xs font-medium leading-none',
  small: 'h-5 gap-1 px-2 py-1 text-[11px] font-medium leading-none',
};

const radiusClasses = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  default: 'rounded-[8px]',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
};

const iconSizeMap = { small: 12, medium: 14 };

/**
 * Chip / tag aligned with ann `@ann/web-components` Tag (variants, size, Lucide icon, dismiss, spinner, click).
 *
 * Icons: import in the app (`import { Check } from 'lucide-react'`) and pass `icon={Check}`.
 *
 * @param {object} props
 * @param {import('react').ReactNode} [props.children]
 * @param {'default'|'primary'|'classicPrimary'|'success'|'warning'|'danger'|'secondary'|'neutral'|'informative'|'error'|'alert'|'limeGreen'|'yellow'|'purple'|'lightBlue'|'orange'} [props.variant='default']
 * @param {'small'|'medium'} [props.size='medium']
 * @param {'none'|'sm'|'default'|'md'|'lg'|'xl'|'2xl'|'full'} [props.radius='default']
 * @param {import('lucide-react').LucideIcon} [props.icon]
 * @param {boolean} [props.disabled]
 * @param {boolean} [props.closable]
 * @param {(id?: string) => void} [props.onClose]
 * @param {string} [props.id] — forwarded to `onClose(id)` when dismissed.
 * @param {boolean} [props.spinner]
 * @param {boolean} [props.clickable] — `cursor-pointer` even without `onClick` (interactive affordance).
 * @param {(e: import('react').MouseEvent) => void} [props.onClick] — root click; close button calls `stopPropagation`.
 * @param {string} [props.closeButtonAriaLabel]
 * @param {string} [props.iconClassName] - Extra classes for the left icon.
 * @param {string} [props.className]
 */
const Badge = ({
  children,
  variant = 'default',
  size = 'medium',
  radius = 'default',
  className = '',
  icon: Icon,
  disabled = false,
  closable = false,
  onClose,
  id,
  spinner = false,
  clickable = false,
  onClick,
  closeButtonAriaLabel = 'Close',
  iconClassName = '',
}) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  const sz = size === 'small' ? 'small' : 'medium';
  const iconSize = iconSizeMap[sz];
  const v = variantClasses[variant] ? variant : 'default';
  const radiusClass = radiusClasses[radius] ?? radiusClasses.default;

  const handleClose = (e) => {
    e.stopPropagation();
    onClose?.(id);
    setDismissed(true);
  };

  const handleRootClick = (e) => {
    if (disabled) return;
    onClick?.(e);
  };

  const showPointer = !disabled && (clickable || onClick);

  return (
    <div
      className={[
        'box-border inline-flex max-w-full items-center whitespace-nowrap',
        radiusClass,
        sizeClasses[sz],
        variantClasses[v],
        disabled ? 'cursor-not-allowed opacity-50' : '',
        showPointer ? 'cursor-pointer' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={onClick ? handleRootClick : undefined}
    >
      {Icon && <Icon size={iconSize} className={['shrink-0', iconClassName].filter(Boolean).join(' ')} aria-hidden />}
      {children != null && children !== false ? children : null}
      {spinner && (
        <Loader2
          size={iconSize}
          className="shrink-0 animate-spin"
          aria-hidden
        />
      )}
      {closable && !disabled && (
        <button
          type="button"
          onClick={handleClose}
          className="-mr-0.5 inline-flex shrink-0 rounded p-0.5 text-current hover:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2173FF] focus-visible:ring-offset-1 dark:hover:bg-white/15 dark:focus-visible:ring-blue-400"
          aria-label={closeButtonAriaLabel}
        >
          <X size={iconSize} strokeWidth={2} />
        </button>
      )}
    </div>
  );
};

Badge.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf([
    'default',
    'primary',
    'classicPrimary',
    'success',
    'warning',
    'danger',
    'secondary',
    'neutral',
    'informative',
    'error',
    'alert',
    'limeGreen',
    'yellow',
    'purple',
    'lightBlue',
    'orange',
  ]),
  size: PropTypes.oneOf(['small', 'medium']),
  radius: PropTypes.oneOf(['none', 'sm', 'default', 'md', 'lg', 'xl', '2xl', 'full']),
  icon: PropTypes.elementType,
  disabled: PropTypes.bool,
  closable: PropTypes.bool,
  onClose: PropTypes.func,
  id: PropTypes.string,
  spinner: PropTypes.bool,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  closeButtonAriaLabel: PropTypes.string,
  iconClassName: PropTypes.string,
  className: PropTypes.string,
};

export default Badge;
